import type { MailboxInfo } from 'owa-client-types';
import {
    setTryRemoveFromMailStoreItemsFunction,
    type RemoveItemSource,
} from 'owa-mail-actions/lib/triage/tryRemoveFromMailStoreItems';
import getAllTableViewsContainingItems from 'owa-mail-triage-table-utils/lib/getAllTableViewsContainingItems';
import { getRowKeysFromRowIds, isMailListConversationView } from 'owa-mail-list-store';
import store from 'owa-mail-store/lib/store/Store';
import { mutatorAction } from 'satcheljs';
import {
    isItemHeldByConversationItemParts,
    isItemHeldByItemReadingPane,
} from 'owa-mail-reading-pane-store';

/* eslint-disable-next-line owa-custom-rules/no-side-effects -- (https://aka.ms/OWALintWiki)
 * This is ok as we are just setting a function variable */
setTryRemoveFromMailStoreItemsFunction(
    (itemIdToRemove: string, removeItemSource: RemoveItemSource, skipMutator?: boolean) => {
        const itemId = itemIdToRemove;
        const item = store().items.get(itemId);

        // If the item has already been removed, NOOP
        // Note this can happen right now because reading pane code calls items.delete(itemId) multiple times for the same delete
        // from deleteConversationItemParts and cleanUpItemClientSide.
        if (!item) {
            return;
        }

        const parentConversationId = item.ConversationId?.Id;

        if (
            isItemHeldByConversationItemParts(parentConversationId, removeItemSource) ||
            isItemHeldByItemReadingPane(itemId, removeItemSource) ||
            isItemHeldByListViewTable(itemId, item.MailboxInfo, item.ParentFolderId?.Id)
        ) {
            // The item reference is currently held, we cannot remove it at this time
            return;
        }

        // Delete the item since it's no longer referenced
        (skipMutator ? deleteMailStoreItem : deleteMailStoreItemMutator)(itemId);
    }
);

const deleteMailStoreItemMutator = mutatorAction('deleteMailStoreItem', deleteMailStoreItem);

function deleteMailStoreItem(itemId: string): void {
    store().items.delete(itemId);
}

function isItemHeldByListViewTable(
    itemId: string,
    mailboxInfo: MailboxInfo,
    parentFolderId: string | undefined
): boolean {
    if (!parentFolderId) {
        return false;
    }

    // Don't check removeItemSource here. Even if it's from a listview table,
    // another listview table may be holding on to this item

    const tableViewsForFolder = getAllTableViewsContainingItems(
        mailboxInfo,
        parentFolderId,
        true /* shouldIncludeSearchTable */
    );

    for (const tableView of tableViewsForFolder) {
        if (isMailListConversationView(tableView)) {
            // Only message listviews can hold references to message items
            continue;
        }

        const rowKeys = getRowKeysFromRowIds([itemId], tableView);
        for (const rowKey of rowKeys) {
            if (tableView.rowKeys.indexOf(rowKey) >= 0) {
                return true;
            }
        }
    }

    return false;
}
